import type {Dispatch, GetState, ThunkAction} from 'src/reducers';

import type {
  ScheduledEvents,
  Filters,
  ScheduledEvent,
} from 'src/types/scheduler/events';

import {camel, snake} from 'src/utils';
import * as reduxApi from 'src/utils/redux-api-v2';

import {addDays, getTime, subDays} from 'date-fns';

import {
  selectPastEventsAPIParams,
  selectUpcomingEventsAPIParams,
} from 'src/selectors/scheduler/events';


export type SchedulerEventActions =
  | SetPastEventsAction
  | AppendPastEventsAction
  | SetPastEventsFilters
  | SetUpcomingEventsAction
  | AppendUpcomingEventsAction
  | SetUpcomingEventsFilters
  | AddNewUpcomingEventAction
  | DeleteUpcomingEventAction
  | UpdateEventsStatusAction;

export const SET_PAST_EVENTS = 'scheduling/events/setPastEvents';
export const APPEND_PAST_EVENTS = 'scheduling/events/appendPastEvents';
export const SET_PAST_EVENTS_FILTERS = 'scheduling/events/setPastEventsFilters';
export const SET_UPCOMING_EVENTS = 'scheduling/events/setUpcomingEvents';
export const APPEND_UPCOMING_EVENTS = 'scheduling/events/appendUpcomingEvents';
export const SET_UPCOMING_EVENTS_FILTERS =
  'scheduling/events/setUpcomingEventsFilters';
export const ADD_NEW_UPCOMING_EVENT = 'scheduling/events/addNewUpcomingEvent';
export const DELETE_UPCOMING_EVENT = 'scheduling/events/deleteUpcomingEvent';
export const UPDATE_EVENTS_STATUS = 'scheduling/events/updateEventsStatus';

type eventPayload = {
  data: ScheduledEvents;
  totalPages: number;
};
export type SetPastEventsAction = {
  type: typeof SET_PAST_EVENTS;
  payload: eventPayload;
};

export const setPastEvents = (payload: eventPayload): SetPastEventsAction => ({
  type: SET_PAST_EVENTS,
  payload,
});

export type AppendPastEventsAction = {
  type: typeof APPEND_PAST_EVENTS;
  payload: eventPayload;
};

export const appendPastEvents = (
  payload: eventPayload,
): SetPastEventsAction => ({
  // @ts-expect-error - TS2322 - Type '"scheduling/events/appendPastEvents"' is not assignable to type '"scheduling/events/setPastEvents"'.
  type: APPEND_PAST_EVENTS,
  payload,
});

export type SetPastEventsFilters = {
  type: typeof SET_PAST_EVENTS_FILTERS;
  payload: Filters;
};

export const setPastEventsFilters = (
  payload: Filters,
): SetPastEventsFilters => ({
  type: SET_PAST_EVENTS_FILTERS,
  payload,
});

export type SetUpcomingEventsAction = {
  type: typeof SET_UPCOMING_EVENTS;
  payload: eventPayload;
};

export const setUpcomingEvents = (
  payload: eventPayload,
): SetUpcomingEventsAction => ({
  type: SET_UPCOMING_EVENTS,
  payload,
});

export type AppendUpcomingEventsAction = {
  type: typeof APPEND_UPCOMING_EVENTS;
  payload: eventPayload;
};

export const appendUpcomingEvents = (
  payload: eventPayload,
): AppendUpcomingEventsAction => ({
  type: APPEND_UPCOMING_EVENTS,
  payload,
});

export type SetUpcomingEventsFilters = {
  type: typeof SET_UPCOMING_EVENTS_FILTERS;
  payload: Filters;
};

export const setUpcomingEventsFilters = (
  payload: Filters,
): SetUpcomingEventsFilters => ({
  type: SET_UPCOMING_EVENTS_FILTERS,
  payload,
});

export type AddNewUpcomingEventAction = {
  type: typeof ADD_NEW_UPCOMING_EVENT;
  payload: ScheduledEvent;
};

export const addNewUpcomingEvent = (
  payload: ScheduledEvent,
): AddNewUpcomingEventAction => ({
  type: ADD_NEW_UPCOMING_EVENT,
  payload,
});

export type DeleteUpcomingEventAction = {
  type: typeof DELETE_UPCOMING_EVENT;
  payload: string;
};

export type UpdateEventsStatusAction = {
  type: typeof UPDATE_EVENTS_STATUS;
  payload: {
    [key: string]: {
      key: string;
      value: boolean;
    };
  };
};

export const deleteUpcomingEvent = (
  eventId: string,
): DeleteUpcomingEventAction => ({
  type: DELETE_UPCOMING_EVENT,
  payload: eventId,
});

export const updateEventsStatus = (payload: {
  [key: string]: {
    key: string;
    value: boolean;
  };
}): UpdateEventsStatusAction => ({
  type: UPDATE_EVENTS_STATUS,
  payload,
});

export const fetchPastEvents =
  (): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
    const pastEventParams = selectPastEventsAPIParams(getState());
    const startAt = `${Math.floor(getTime(subDays(new Date(), 365)) / 1000)}`; // 365 days ago as an integer
    const endAt = `${Math.floor(Date.now() / 1000)}`; // Current time as an integer
    const pastEventsPayload = {...pastEventParams, startAt, endAt} as const;

    try {
      const result = await dispatch(
        reduxApi.get('scheduler/events', snake(pastEventsPayload)),
      );
      if (pastEventParams.page === '1') {
        dispatch(setPastEvents(camel(result)));
      } else {
        dispatch(appendPastEvents(camel(result)));
      }
    } catch (err: any) {
      throw err;
    }
  };

export const fetchUpcomingEvents =
  (): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
    const upcomingEventParams = selectUpcomingEventsAPIParams(getState());
    const startAt = `${Math.floor(Date.now() / 1000)}`; // Current time in seconds as an integer
    const endAt = `${Math.floor(getTime(addDays(new Date(), 365)) / 1000)}`; // 365 days from now as an integer
    const upcomingEventsPayload = {
      ...upcomingEventParams,
      startAt,
      endAt,
    } as const;

    try {
      const result = await dispatch(
        reduxApi.get('scheduler/events', snake(upcomingEventsPayload)),
      );
      if (upcomingEventParams.page === '1') {
        dispatch(setUpcomingEvents(camel(result)));
      } else {
        dispatch(appendUpcomingEvents(camel(result)));
      }
    } catch (err: any) {
      throw err;
    }
  };
